import _ from 'lodash'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { TPAWidget } from '@wix/document-services-types'
import { CarmiInstance, EditorFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import type {
	IViewerManagerImplementor,
	ClientSpecMap,
	SitePropertiesInfo,
	PagesPlatformApplications,
	SiteMetaData,
	RoutersMap,
} from 'thunderbolt-viewer-manager-types'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import type { ViewerManagerRendererModelApiEditorConfig } from './types'
import type { Language } from '@wix/thunderbolt-becky-types'
import { transformToTbStructure } from './pagesPlatformApplications'
import { name } from './symbols'

const updateSpecMapWithOverrides = (originalCSM: ClientSpecMap, ooiInEditorOverrides: string): ClientSpecMap => {
	if (ooiInEditorOverrides) {
		const csmWithOOIInEditor = _.cloneDeep(originalCSM)
		Object.values(csmWithOOIInEditor).forEach((app) => {
			if ('widgets' in app) {
				Object.keys(app.widgets).forEach((currWidgetId) => {
					const widget = app.widgets[currWidgetId]
					if (ooiInEditorOverrides.includes(currWidgetId) && isExtendedTPAWidget(widget)) {
						widget.componentFields!.ooiInEditor = ooiInEditorOverrides.includes(`${currWidgetId}=true`)
					}
				})
			}
		})

		return csmWithOOIInEditor
	}

	return originalCSM
}

const isExtendedTPAWidget = (widget: any): widget is TPAWidget & { componentFields: { ooiInEditor: boolean } } =>
	'componentFields' in widget

export const ViewerManagerRendererModelApi = withDependencies(
	[named(EditorFeatureConfigSymbol, name), CarmiInstance],
	(
		rendererModelApiConfig: ViewerManagerRendererModelApiEditorConfig,
		{ rendererModel, language, $runInBatch }: DSCarmi
	): IViewerManagerImplementor<'rendererModel'> => {
		const getClientSpecMap = () => (rendererModelApiConfig.clientSpecMap as unknown) as ClientSpecMap

		return {
			rendererModel: {
				updateClientSpecMap: (clientSpecMap) => {
					const specMapWithOverrides = updateSpecMapWithOverrides(
						clientSpecMap,
						rendererModelApiConfig.ooiInEditorOverrides
					)
					rendererModel.updateClientSpecMap(specMapWithOverrides)
					rendererModel.updateClientSpecMapForRoot(specMapWithOverrides) // update also root until we figure out how to fix the clientSpecMap issue
				},
				getClientSpecMap,
				updateWixCodeModel: (wixCodeModel) => {
					rendererModel.updateWixCodeModel(wixCodeModel)
				},
				getWixCodeModel: () => rendererModelApiConfig.wixCodeModel,
				updatePagesPlatformApplications: (pagesPlatformApplications) => {
					// transforming pagesPlatformApplications here until DS will remove the transformation on their side
					rendererModel.updatePagesPlatformApplications(transformToTbStructure(pagesPlatformApplications))
				},
				getPagesPlatformApplications: () =>
					(rendererModelApiConfig.pagesPlatformApplications as unknown) as PagesPlatformApplications,
				updatePasswordProtectedPages: (passwordProtectedPages: Array<string>) => {
					rendererModel.updatePasswordProtectedPages(passwordProtectedPages)
				},
				getPasswordProtectedPages: () => rendererModelApiConfig.passwordProtectedPages,
				updateSitePropertiesInfo: (sitePropertiesInfo) => {
					$runInBatch(() => {
						rendererModel.updateSitePropertiesInfo(sitePropertiesInfo)
						const originalLanguage = sitePropertiesInfo.multilingualInfo?.originalLanguage?.languageCode
						const mainLanguage = sitePropertiesInfo.language as Language
						if (originalLanguage) {
							language.setOriginalLanguage(originalLanguage as Language)
						} else {
							// when there is no original language it means ML is not installed so the original language
							// should equal to current language in order to not apply data from translations
							language.setOriginalLanguage(mainLanguage as Language)
							language.setLanguage(mainLanguage)
						}
					})
				},
				getSitePropertiesInfo: () =>
					(rendererModelApiConfig.sitePropertiesInfo as unknown) as SitePropertiesInfo,
				updateMetaSiteId: (metaSiteId) => {
					rendererModel.updateMetaSiteId(metaSiteId)
					rendererModel.updateMetaSiteIdForRoot(metaSiteId)
				},
				getMetaSiteId: () => rendererModelApiConfig.metaSiteId,
				updatePremiumFeatures: (premiumFeatures) => {
					rendererModel.updatePremiumFeatures(premiumFeatures)
				},
				getPremiumFeatures: () => rendererModelApiConfig.premiumFeatures,
				updateRouters: (routers) => {
					rendererModel.updateRouters(routers)
					rendererModel.updateRoutersInfo(routers ? routers.configMap : {})
				},
				getRouters: () => (rendererModelApiConfig.routers as unknown) as RoutersMap,
				updateSiteInfo: (siteInfo) => {
					rendererModel.updateSiteInfo({
						...siteInfo,
						...(siteInfo.documentType === 'Template' && { documentType: 'TemplateBeforeSave' }),
					})
				},
				getSiteInfo: () => {
					return {
						...rendererModelApiConfig.siteInfo,
						// @ts-expect-error 'TemplateBeforeSave' is a runtime type for differentiating between
						// template before first save vs template viewer. It is not saved in the document
						...(rendererModelApiConfig.siteInfo.documentType === 'TemplateBeforeSave' && {
							documentType: 'Template',
						}),
					}
				},
				updateSiteMetaData: (siteMetaData) => {
					rendererModel.updateSiteMetaData(siteMetaData)
				},
				getSiteMetaData: () => (rendererModelApiConfig.siteMetaData as unknown) as SiteMetaData,
				updatePlatformAppsExperiments: (platformAppsExperiments) => {
					rendererModel.updatePlatformAppsExperiments(platformAppsExperiments)
				},
			},
		}
	}
)
